<script>
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import SveltyPicker from "svelty-picker";
	import CustomButton from "../../../components/CustomButton/CustomButton.svelte";
	import CustomSearch from "../../../components/CustomSearch/CustomSearch.svelte";
	import { createWorkOrderJob } from "src/services/maintenanceRepair";
	import { toastMessage } from "src/store/store";

	export let workOrderId;
	export let workOrder;
	export let onWorkOrderChange = () => {};
	export let onCancelAddJob = () => {};

	const presetTaskOptions = [
		{ value: "home-lot-to-workshop", label: "Home lot to Workshop" },
		{ value: "workshop-to-home-lot", label: "Workshop to Home lot" },
		{ value: "vehicle-inspection", label: "Vehicle inspection" },
		{ value: "part-replacement", label: "Part replacement" },
		{ value: "engine-service", label: "Engine service" },
		{ value: "tyre-replacement", label: "Tyre replacement" },
	];

	const priorityOptions = [
		{ value: "low", label: "Low" },
		{ value: "medium", label: "Medium" },
		{ value: "high", label: "High" },
	];

	function emptyRow() {
		return { task: undefined, priority: undefined, description: "" };
	}

	let rows = [emptyRow()];

	function handleRowAction(index) {
		if (rows.length > 1) {
			rows = rows.filter((_, i) => i !== index);
		} else {
			rows = [emptyRow()];
		}
	}

	function addRow() {
		rows = [...rows, emptyRow()];
	}

	let assignedToType = "user";
	let assignedTo = undefined;

	let startDateTime;
	let endDateTime;

	let blackoutVehicle = true;

	// TODO: swap for real `client()` calls to `POST /v0/Auth/GetUsersWithFilter`
	// and `POST /v0/Group/GetWithFilters` (see JobManagement/Assign.svelte's
	// fetchActiveUsers/fetchActiveGroups) — stand-in options until then.
	const userOptions = [
		{ value: "samlim@getgo.sg", label: "Sam Lim" },
		{ value: "johnsmith@getgo.sg", label: "John Smith" },
		{ value: "amirah.rahman@getgo.sg", label: "Amirah Rahman" },
		{ value: "opsmgr@getgo.sg", label: "Ops Manager" },
	];

	const groupOptions = [
		{ value: "ops-north", label: "Ops North" },
		{ value: "ops-south", label: "Ops South" },
		{ value: "workshop-abc", label: "Workshop ABC" },
	];

	$: assignedToOptions =
		assignedToType === "user" ? userOptions : groupOptions;

	function handleAssignedToSearch(input) {
		if (!input) return Promise.resolve(assignedToOptions);
		return Promise.resolve(
			assignedToOptions.filter((option) =>
				option.label.toLowerCase().includes(input.toLowerCase())
			)
		);
	}

	function handleAssignedToTypeChange() {
		assignedTo = undefined;
	}

	function handleTaskSearch(input) {
		if (!input) return Promise.resolve(presetTaskOptions);
		return Promise.resolve(
			presetTaskOptions.filter((option) =>
				option.label.toLowerCase().includes(input.toLowerCase())
			)
		);
	}

	function handlePrioritySearch(input) {
		if (!input) return Promise.resolve(priorityOptions);
		return Promise.resolve(
			priorityOptions.filter((option) =>
				option.label.toLowerCase().includes(input.toLowerCase())
			)
		);
	}

	function isBlankRow(row) {
		return !row.task && !row.priority && !row.description?.trim();
	}

	function completedRows() {
		return rows.filter((row) => row.task && row.priority);
	}

	// Fully blank extra rows are ignored, but a partly filled one blocks
	// submission rather than being silently dropped.
	$: filledRows = rows.filter((row) => !isBlankRow(row));
	$: startTime = startDateTime ? new Date(startDateTime).getTime() : NaN;
	$: endTime = endDateTime ? new Date(endDateTime).getTime() : NaN;
	$: hasValidDates =
		Number.isFinite(startTime) &&
		Number.isFinite(endTime) &&
		endTime > startTime;
	$: showDateRangeError =
		Number.isFinite(startTime) &&
		Number.isFinite(endTime) &&
		endTime <= startTime;
	$: isValid =
		filledRows.length > 0 &&
		filledRows.every((row) => row.task && row.priority) &&
		!!assignedTo &&
		hasValidDates;

	let isSubmitting = false;

	const priorityRank = Object.fromEntries(
		priorityOptions.map((option, index) => [option.value, index])
	);

	function highestPriority(completed) {
		return completed.reduce(
			(highest, row) =>
				highest === undefined ||
				priorityRank[row.priority.value] > priorityRank[highest]
					? row.priority.value
					: highest,
			undefined
		);
	}

	async function handleSubmit() {
		if (isSubmitting || !isValid) return;
		isSubmitting = true;

		try {
			const completed = completedRows();
			const tasks = completed.map((row) => ({
				name: row.task.label,
				plannerRemarks: row.description,
				priority: row.priority.value,
			}));

			const res = await createWorkOrderJob(workOrderId, {
				// No job-level priority field in this form yet — the highest
				// task priority stands in until one's added.
				priority: highestPriority(completed),
				start: new Date(startTime).toISOString(),
				due: new Date(endTime).toISOString(),
				assignedTo: {
					type: assignedToType === "user" ? "User" : "Group",
					id: assignedTo.value,
					display: assignedTo.label,
				},
				createdBy: "opsmgr@getgo.sg",
				createBlackout: blackoutVehicle,
				tasks,
			});

			if (res.error) {
				toastMessage.set({
					type: "danger",
					message: "Something went wrong. Please try again.",
				});
				return;
			}

			onWorkOrderChange({
				...workOrder,
				jobs: [...(workOrder?.jobs || []), res.data],
			});
			toastMessage.set({
				type: "success",
				message: "Job successfully added",
			});
			onCancelAddJob();
		} finally {
			isSubmitting = false;
		}
	}
</script>

<Row>
	<Col xs={12}>
		<Row>
			<Col class="row d-flex align-items-center mx-0 px-0">
				<h5 class="fw-bold mb-0">Add job</h5>
			</Col>
			<Col class="d-flex flex-row-reverse" style="height: 70px">
				<div class="mx-2 primary-btn-fix">
					<CustomButton buttonText="Add a row" on:click={addRow} />
				</div>
			</Col>
		</Row>
	</Col>

	<Col xs={12}>
		<table class="task-table">
			<thead>
				<tr>
					<th class="task-th">Task</th>
					<th class="priority-th">Priority</th>
					<th>Description</th>
					<th class="actions-th">Action</th>
				</tr>
			</thead>
			<tbody>
				{#each rows as row, index}
					<tr>
						<td>
							<CustomSearch
								items={presetTaskOptions}
								loadOptions={handleTaskSearch}
								placeholder="Select task"
								bind:value={row.task}
								isClearable={false}
								chevron={true}
								containerClasses="search-border"
							/>
						</td>
						<td>
							<CustomSearch
								items={priorityOptions}
								loadOptions={handlePrioritySearch}
								placeholder="Select priority"
								bind:value={row.priority}
								isClearable={false}
								chevron={true}
								containerClasses="search-border"
							/>
						</td>
						<td>
							<label
								for="job-description-{index}"
								class="visually-hidden"
							>
								Description for task {index + 1}
							</label>
							<input
								id="job-description-{index}"
								type="text"
								class="form-control getgo input search-border"
								placeholder="Provide additional context about the task"
								bind:value={row.description}
							/>
						</td>
						<td class="xs-td actions-td">
							<button
								type="button"
								class="delete"
								on:click={() => handleRowAction(index)}
							>
								<img
									src="/assets/icon-trash.svg"
									alt={rows.length > 1
										? "Remove row"
										: "Clear row"}
								/>
							</button>
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</Col>

	<Col xs={12} class="mt-4">
		<div class="mb-2">
			Assign to<span class="required-asterisk">*</span>
		</div>
		<div class="mb-4 d-flex flex-column">
			<label class="mb-2">
				<input
					type="radio"
					name="assigned-to-type"
					value="user"
					class="mx-2"
					bind:group={assignedToType}
					on:change={handleAssignedToTypeChange}
				/>
				<span>A single user</span>
			</label>
			<label>
				<input
					type="radio"
					name="assigned-to-type"
					value="group"
					class="mx-2"
					bind:group={assignedToType}
					on:change={handleAssignedToTypeChange}
				/>
				<span>A group</span>
			</label>
		</div>
		<Row>
			<Col xs={4}>
				<div class="mb-1">
					{#if assignedToType === "user"}
						<span
							>Select a user<span class="required-asterisk"
								>*</span
							></span
						>
					{:else}
						<span
							>Select a group<span class="required-asterisk"
								>*</span
							></span
						>
					{/if}
				</div>
				<div class="assigned-to-search">
					<CustomSearch
						items={assignedToOptions}
						loadOptions={handleAssignedToSearch}
						placeholder={assignedToType === "user"
							? "e.g. John Doe"
							: "Select a group"}
						bind:value={assignedTo}
						isClearable={false}
						chevron={false}
						containerClasses="search-icon"
					/>
				</div>
			</Col>
		</Row>
	</Col>

	<Col xs={12} class="mt-4">
		<div class="date-time-row">
			<Row>
				<Col xs={4}>
					<div class="mb-1">
						Start date and time<span class="required-asterisk"
							>*</span
						>
					</div>
					<SveltyPicker
						inputClasses="form-control getgo input calendar-icon"
						format="yyyy-mm-dd hh:ii"
						placeholder="Select a date and time"
						bind:value={startDateTime}
					/>
				</Col>
				<Col xs={4}>
					<div class="mb-1">
						End date and time<span class="required-asterisk">*</span
						>
					</div>
					<SveltyPicker
						inputClasses="form-control getgo input calendar-icon"
						format="yyyy-mm-dd hh:ii"
						placeholder="Select a date and time"
						bind:value={endDateTime}
					/>
					{#if showDateRangeError}
						<small class="date-range-error">
							End date and time must be after the start.
						</small>
					{/if}
				</Col>
			</Row>
		</div>
	</Col>

	<Col xs={12} class="mt-4">
		<div class="mb-2">
			Blackout vehicle after adding job?<span class="required-asterisk"
				>*</span
			>
		</div>
		<div class="d-flex flex-column">
			<label class="mb-2">
				<input
					type="radio"
					name="blackout-vehicle"
					value={true}
					class="mx-2"
					bind:group={blackoutVehicle}
				/>
				<span>Yes</span>
			</label>
			<label>
				<input
					type="radio"
					name="blackout-vehicle"
					value={false}
					class="mx-2"
					bind:group={blackoutVehicle}
				/>
				<span>No</span>
			</label>
		</div>
	</Col>

	<Col xs={12} class="mt-4">
		<Row>
			<Col class="d-flex flex-row-reverse">
				<div class="mx-2 primary-btn-fix">
					<CustomButton
						buttonText="Add"
						color="primary"
						disabled={!isValid || isSubmitting}
						on:click={handleSubmit}
					/>
				</div>
				<div class="mx-2">
					<CustomButton
						buttonText="Cancel"
						color="secondary"
						on:click={onCancelAddJob}
					/>
				</div>
			</Col>
		</Row>
	</Col>
</Row>

<style>
	.required-asterisk,
	.date-range-error {
		color: var(--fds-error, #d60c0c);
	}

	/* CustomButton's .fds-button.btn-primary rule (background-color/color,
	   no !important) loses to Bootstrap's own .btn-primary in practice —
	   scoped here rather than fixed in the shared component. */
	.primary-btn-fix :global(.fds-button.btn-primary) {
		background-color: var(--fds-primary) !important;
		color: white !important;
	}

	/* Matches App.css's .btn.fds-button:disabled convention, which the
	   override above would otherwise blanket-cancel for disabled buttons. */
	.primary-btn-fix :global(.fds-button.btn-primary:disabled) {
		background-color: #e8e8e8 !important;
		color: var(--fds-on-surface-container-variant) !important;
		border-color: transparent !important;
	}

	.task-table {
		width: 100%;
		border-collapse: collapse;
	}

	.task-table th,
	.task-table td {
		border: 1px solid var(--fds-outline-variant);
	}

	.task-table th {
		text-align: left;
		padding: 8px;
		font-size: 14px;
		color: var(--fds-on-surface-variant, #595959);
	}

	.task-table td {
		padding: 8px;
		vertical-align: middle;
	}

	.xs-td {
		width: 1%;
		white-space: nowrap;
	}

	.task-th {
		width: 40%;
	}

	.priority-th {
		width: 15%;
	}

	.actions-th,
	.actions-td {
		text-align: right;
	}

	button.delete {
		background: none;
		border: none;
		cursor: pointer;
	}

	button.delete:hover {
		opacity: 0.6;
	}

	.task-table input[type="text"] {
		border: none;
		box-shadow: none;
		padding: 0;
	}

	.task-table :global(.form-select.getgo.select) {
		border: none !important;
		box-shadow: none !important;
	}

	.task-table :global(input.svelte-14r22mt) {
		padding: 0 32px 0 0 !important;
	}

	.assigned-to-search :global(input.svelte-14r22mt) {
		padding: 0 12px 0 40px !important;
	}

	.date-time-row :global(.calendar-icon) {
		padding-left: 40px !important;
	}
</style>
